import _get from 'lodash/get';
import { factory as splitFactory, store } from 'src/main';

const SPLIT_IO_TREATMENT = 'enterprise_v2_auth';
const AUTH_METHOD_KEY = 'sl-auth-method';

// Local testing override: set to true/false to bypass Split.io, or null to use Split.io
const LOCAL_OVERRIDE: boolean | null = null;

// ============================================================================
// sl-auth-method WRITE POLICY
// ----------------------------------------------------------------------------
// All writes to localStorage['sl-auth-method'] MUST go through setAuthMethod()
// or clearAuthMethod() in this file — never localStorage.setItem directly.
// Both helpers require a `source` string identifying the caller; the list
// below enumerates the authoritative call sites.
//
// Authoritative writers (every other caller is a bug):
//   AuthMethodSource value           | Site                                    | Method
//   ---------------------------------|----------------------------------------|--------
//   'sign-in:v1-federated-callback'  | SignInV2Container.tsx (handleV1Callback)| 'v1'
//   'sign-in:v1-password'            | SignInV2Container.tsx (handleV1Password)| 'v1'
//   'sign-in:v2-division-issued'     | SignInV2Container.tsx (handleDivision)  | 'v2'
//   'sign-up:invite'                 | SignUp.jsx                              | 'v1'
//   'self-heal:v2-cookie-alive'      | attemptV2SelfHealFromCookie (below)     | 'v2'
//   'sign-out'                       | SignOut.tsx                             | clear
//
// Anything else writing this key is a regression. Grep `setAuthMethod\(` /
// `clearAuthMethod\(` / `'sl-auth-method'` to audit.
// ============================================================================

export type AuthMethodSource =
  | 'sign-in:v1-federated-callback'
  | 'sign-in:v1-password'
  | 'sign-in:v2-division-issued'
  | 'sign-up:invite'
  | 'self-heal:v2-cookie-alive'
  | 'sign-out';

export function setAuthMethod(method: 'v1' | 'v2', _source: AuthMethodSource): void {
  try {
    localStorage.setItem(AUTH_METHOD_KEY, method);
  } catch {
    /* ignore */
  }
}

export function clearAuthMethod(_source: AuthMethodSource): void {
  try {
    localStorage.removeItem(AUTH_METHOD_KEY);
  } catch {
    /* ignore */
  }
}

export function getAuthMethod(): string | null {
  try { return localStorage.getItem(AUTH_METHOD_KEY); } catch { return null; }
}

/**
 * Evaluate the Split.io treatment for a specific email.
 * Used on enterprise-sso after the user enters their email in the sign-in form.
 */
export function evaluateV2ForEmail(email: string): boolean {
  if (LOCAL_OVERRIDE !== null) return LOCAL_OVERRIDE;

  const splitClient = splitFactory.client();
  const urlParams = new URLSearchParams(window.location.search);

  return (
    splitClient.getTreatment(SPLIT_IO_TREATMENT, {
      host: window.location.hostname,
      referrer: urlParams.get('referrer') || '',
      email,
    }) === 'on'
  );
}

/**
 * Determine whether to use the V2 auth flow on app hosts.
 *
 * Not a React hook — pure helper that reads localStorage, Redux store, and
 * Split.io synchronously. Safe to call from handlers, callbacks, and
 * non-component code.
 *
 * Evaluation order:
 * 1. LOCAL_OVERRIDE (dev bypass)
 * 2. sl-auth-method from localStorage (authoritative for current session — prevents mid-session switching)
 * 3. Split.io with email from Redux (only effective before sl-auth-method is set, e.g. on enterprise-sso)
 * 4. Default: V1. Auth routes (/sign_in, /sign_up_invite, etc.) short-circuit
 *    to AuthShell in main.tsx, so this fallback only fires for direct
 *    navigation to an app path while signed-out — both shells then 401-redirect
 *    through AuthShell anyway, so V1 is the safer default.
 */
export function isV2Enabled(): boolean {
  if (LOCAL_OVERRIDE !== null) return LOCAL_OVERRIDE;

  const storedMethod = getAuthMethod();
  if (storedMethod === 'v2') return true;
  if (storedMethod === 'v1') return false;

  const userEmail = _get(store.getState(), ['user', 'session', 'email'], '');
  if (!userEmail) {
    return false;
  }

  const splitClient = splitFactory.client();
  const urlParams = new URLSearchParams(window.location.search);
  const divisionId = _get(store.getState(), ['user', 'session', 'divisionId'], '');
  const beaconClientId = _get(store.getState(), ['user', 'config', 'vendor', 'BeaconClientId'], '');

  return (
    splitClient.getTreatment(SPLIT_IO_TREATMENT, {
      host: window.location.hostname,
      referrer: urlParams.get('referrer') || '',
      email: userEmail,
      divisionId,
      beaconClientId
    }) === 'on'
  );
}

/**
 * On-mount self-heal for V2 sessions whose sl-auth-method localStorage flag is missing.
 *
 * Background: shell selection in main.tsx#AppSwitch captures useV2Flow synchronously
 * via useState at first render. At that moment Redux has no email yet, so isV2Enabled()
 * falls back to V1. The intended invariant is that SignInV2Container persists
 * sl-auth-method='v2' so subsequent mounts short-circuit on it. If that flag is missing
 * (cleared during a logout test, predates the setAuthMethod call site, etc.) but a
 * valid sl-auth-token cookie is still alive, the user silently mounts as AppV1 and
 * loses V2-only behavior (useDivisionCheck tab-focus validate, V2 division dropdown,
 * etc.).
 *
 * This helper probes /api/auth/v2/validate. If authenticated, sets sl-auth-method='v2'
 * and reloads once so AppSwitch picks AppV2. A sessionStorage sentinel prevents reload
 * loops if setAuthMethod silently fails (e.g. localStorage unavailable). Sentinel
 * lives in sessionStorage so a fresh tab/window gets another heal attempt.
 *
 * Skips when:
 *  - on an auth route (AuthShell handles those, no heavy shell decision to fix)
 *  - on enterprise-sso host (its own auth flow, no heavy shell)
 *  - sl-auth-method is already set (caller has explicit intent — don't override)
 *  - sentinel already set (avoid reload loops)
 */
const SELF_HEAL_SENTINEL_KEY = 'sl-auth-method-self-heal-attempted';

export function attemptV2SelfHealFromCookie(authRoutePathnames: ReadonlySet<string>): void {
  const onAuthRoute = authRoutePathnames.has(window.location.pathname);
  const onEnterpriseSso = window.location.hostname.includes('enterprise-sso');
  const alreadyAttempted = (() => {
    try { return sessionStorage.getItem(SELF_HEAL_SENTINEL_KEY) === '1'; } catch { return false; }
  })();
  const eligible = !onAuthRoute && !onEnterpriseSso && getAuthMethod() === null && !alreadyAttempted;

  if (!eligible) {
    return;
  }

  import('src/store/modules/auth/v2Operations')
    .then(({ v2Validate }) => v2Validate())
    .then((response) => {
      if (!response.authenticated) return;

      try { sessionStorage.setItem(SELF_HEAL_SENTINEL_KEY, '1'); } catch { /* ignore */ }
      setAuthMethod('v2', 'self-heal:v2-cookie-alive');
      window.location.reload();
    })
    .catch(() => {
      /* swallow — probe failure leaves session as-is and will retry on next mount */
    });
}
